import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import { InputComponent } from '../../shared/ui/input/input.component';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  LOCAL_LANG,
  REGEX_EMAIL,
  REGEX_PASSWORD_ALLSPECIAL_CHARACTER,
} from 'sources/apps/front-mgr/src/app/core/constants/constants';
import { ActivatedRoute, Router } from '@angular/router';
import { RoutePaths } from 'sources/apps/front-mgr/src/app/core/constants/route-paths';
import { GeneralErrorComponent } from '../../shared/ui/general-error/general-error.component';
import { Subscription } from 'rxjs';
import { map, distinctUntilChanged } from 'rxjs/operators';
import { SpinnerService } from '../../../core/services/spinner.service';
import { AuthenticationService } from '../services/authentication.service';
import { LoginResponse, LoginUserData } from '../type/login';
import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult } from '@azure/msal-browser';
import { DataSessionService } from '../../../core/services/data-session.service';
import { RecaptchaService } from '../../shared/services/recaptch.service';
import { environment } from 'sources/apps/front-mgr/src/environments/environment';
import { DataLayerService } from '../../../core/services/data-layer.service';

@Component({
  selector: 'app-login-form',
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    TranslateModule,
    InputComponent,
    ReactiveFormsModule,
    GeneralErrorComponent,
  ],
  templateUrl: './login-form.component.html',
  styleUrl: './login-form.component.scss',
})
export class LoginFormComponent implements OnInit, OnDestroy {
  loginForm!: FormGroup;
  userData!: LoginUserData;
  errorMess!: string | null;
  userInt: boolean = false;
  subscription: Subscription = new Subscription();
  firstLoad: boolean = false;
  closeSesion: boolean = false;
  showPassword = false;
  passwordType: 'passwordText' | 'password' = 'password';
  @ViewChild('loginVideo', { static: true, read: ElementRef })
  loginVideo?: ElementRef;
  shouldFocusUsername: boolean = false;
  screenName = 'login';

  constructor(
    private readonly _formBuilder: FormBuilder,
    private readonly _router: Router,
    private readonly _authenticationService: AuthenticationService,
    private readonly _spinnerService: SpinnerService,
    private readonly _dataSessionService: DataSessionService,
    private readonly _recaptchaService: RecaptchaService,
    private readonly _msalService: MsalService,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _dataLayerService: DataLayerService
  ) {
    this.closeSesion = this._activatedRoute.snapshot.paramMap.get('close')
      ? true
      : false;

    this.firstLoad = false;
    this.loginForm = this._formBuilder.group({
      username: ['', [Validators.required, Validators.pattern(REGEX_EMAIL)]],
      password: [
        '',
        [
          Validators.required,
          Validators.pattern(REGEX_PASSWORD_ALLSPECIAL_CHARACTER),
        ],
      ],
    });

    this.loginForm
      .get('username')
      ?.valueChanges.pipe(
        map((username: string) => {
          this.shouldFocusUsername = false;
          const email = this.loginForm.get('username')?.value;
          return email?.includes('@')
            ? this._authenticationService.isInternalUserDomain(username)
            : false;
        }),
        distinctUntilChanged()
      )
      .subscribe((isInternalUser: boolean) => {
        this.userInt = isInternalUser;
        this.updatePasswordValidators();
      });

    this.loginForm
      .get('password')
      ?.valueChanges.subscribe((password: string) => {
        if (!password) {
          this.showPassword = false;
          this.passwordType = 'password';
        }
      });
  }

  ngAfterViewInit(): void {
    if (this.loginVideo?.nativeElement) {
      this.loginVideo.nativeElement.muted = true;
      this.loginVideo.nativeElement.play();
    }
  }

  ngOnInit(): void {
    console.log('Deploy Success - v0.0.53 PRE');
    this._dataLayerService.pushEventPageView(this.screenName);

    this._msalService.handleRedirectObservable().subscribe({
      next: (result: AuthenticationResult) => {
        this.firstLoad = false;

        if (result && !this.closeSesion) {
          const { accessToken, account } = result;
          const idTokenClaims = account?.idTokenClaims;
          this._dataSessionService.setTokenAzure(accessToken, idTokenClaims);

          if (
            this._dataSessionService.isValidRole(
              this._dataSessionService.validRoles
            )
          ) {
            this._msalService.instance.setActiveAccount(account);
            this._dataLayerService.pushEventLogin(this.screenName, 'success');
            this.handleRedirectHome();
          } else {
            if (typeof idTokenClaims === 'object' && idTokenClaims) {
              const { email = '' } = idTokenClaims;
              this.loginForm.controls['username'].setValue(email || '');
            }
            this._dataSessionService.setLoggedIn(false);
            this.firstLoad = true;
            this.errorMess =
              this._authenticationService.getErrorMessage('EXC01');
            localStorage.clear();
            this._msalService.instance.clearCache();

            this._dataLayerService.pushEventLogin(
              this.screenName,
              'error',
              'EXC01'
            );
          }
          this._spinnerService.hide();
        } else {
          this.firstLoad = true;
          this._dataSessionService.removeToken();
        }
      },
      error: (error: any) => {
        this.errorMess = this._authenticationService.getErrorMessage('EXC01');
        this._dataLayerService.pushEventLogin(this.screenName, 'error', 'msal');
        this._spinnerService.hide();
      },
    });
  }

  async submitloginForm(): Promise<void> {
    this.errorMess = null;
    this.userData = this.loginForm.value;
    this._spinnerService.show();

    try {
      const reCAPTCHA = await this._recaptchaService.reCAPTCHA('LOGIN');

      if (!this.userInt) {
        this._dataLayerService.pushEventClickButton(
          this.screenName,
          'send',
          'login.action-send'
        );
        const sub = this._authenticationService
          .postLogin(this.userData)
          .subscribe({
            next: (response) => {
              const loginResponse: LoginResponse = response;
              this._dataSessionService.runExternalUser(
                loginResponse.authResult.AuthenticationResult.AccessToken,
                loginResponse.authResult.AuthenticationResult.RefreshToken,
                loginResponse.authResult.AuthenticationResult.IdToken
              );

              this._dataLayerService.pushEventLogin(this.screenName, 'success');
              this.handleRedirectHome();
            },
            error: (err) => {
              this._dataLayerService.pushEventLogin(
                this.screenName,
                'error',
                err.error?.state
              );
              if (err.error.state === 'EXC07') {
                // show modal recaptcha y reload form
                this._dataSessionService.showModalRecaptchaError(() =>
                  this.reloadForm()
                );
              } else {
                this.errorMess = this._authenticationService.getErrorMessage(
                  err.error.state
                );
              }
              this._spinnerService.hide();
            },
            complete: () => {
              this._spinnerService.hide();
            },
          });

        this.subscription.add(sub);
      } else if (reCAPTCHA) {
        this.loginUserInter();
      }
    } catch (error) {
      this._spinnerService.hide();
      this._dataLayerService.pushEventLogin(
        this.screenName,
        'error',
        'recaptcha'
      );
      console.error('Error executing reCAPTCHA', error);
    }
  }

  loginUserInter(): void {
    const loginRequest = {
      scopes: [`${environment.azureclientId}/.default`],
    };
    this._dataLayerService.pushEventClickButton(
      this.screenName,
      'msal-send',
      'login.action-send'
    );

    this._spinnerService.hide();
    this._msalService.loginRedirect(loginRequest).subscribe({
      next: (res) => {
        this._spinnerService.hide();
      },
      error: (error) => {
        this._dataLayerService.pushEventLogin(this.screenName, 'error', 'msal');
        this.errorMess = this._authenticationService.getErrorMessage('');
        this._spinnerService.hide();
      },
    });
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
    this.passwordType = this.showPassword ? 'passwordText' : 'password';
  }

  private async updatePasswordValidators(): Promise<void> {
    const passwordControl = this.loginForm.get('password');
    const emailControl = this.loginForm.get('username');

    if (this.userInt) {
      passwordControl?.clearValidators();
      passwordControl?.updateValueAndValidity({ emitEvent: false });

      emailControl?.clearValidators();
      emailControl?.updateValueAndValidity({ emitEvent: false });
    } else {
      passwordControl?.setValidators([
        Validators.required,
        Validators.pattern(REGEX_PASSWORD_ALLSPECIAL_CHARACTER),
      ]);
      passwordControl?.updateValueAndValidity({ emitEvent: false });
      emailControl?.setValidators([
        Validators.required,
        Validators.pattern(REGEX_EMAIL),
      ]);
      emailControl?.updateValueAndValidity({ emitEvent: false });
    }
    this.shouldFocusUsername = true;
  }

  async handleRedirect(): Promise<void> {
    this._dataLayerService.pushEventClickLink(
      this.screenName,
      'register',
      'login.action-reg'
    );
    await this._router.navigate([`${RoutePaths.registration}`]);
  }

  async handleRedirectForgotPass(): Promise<void> {
    this._dataLayerService.pushEventClickLink(
      this.screenName,
      'forgot-password',
      'login.forget-password'
    );
    await this._router.navigate([`${RoutePaths.changePassword}`]);
  }

  async handleRedirectHome(): Promise<void> {
    await this._router.navigate([`${RoutePaths.home}`]);
    localStorage.setItem(
      LOCAL_LANG,
      this._dataSessionService.userData?.lang || 'es'
    );
    window.location.reload();
  }

  reloadForm(): void {
    this.loginForm.reset();
    this.errorMess = null;
    this._spinnerService.hide();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
